'use client';

import { Box, Paper, Fade } from '@mui/material';
import { Estado, getValveOpen, VALVE_ADDR } from '@/types/proyectos/raceway';
import ValvulaSVG from '@/components/app/proyectos/raceway/ValvulaSVG';
import SensorSVG from '@/components/app/proyectos/raceway/SensorSVG';
import AguaSVG from '@/components/app/proyectos/raceway/AguaSVG';

type RacewaySVGProps = {
  estado: Estado | null;
  onValvula: (valvula: string, estadoActual: boolean) => void;
};

// ─── Sub-componentes internos ─────────────────────────────────────────────────

function FlowArrow({ x, y, angle, color }: { x: number; y: number; angle: number; color: string }) {
  const size = 8;
  return (
    <g transform={`translate(${x},${y}) rotate(${angle})`}>
      <polygon
        points={`${size},0 ${-size * 0.6},${-size * 0.6} ${-size * 0.6},${size * 0.6}`}
        fill={color}
        opacity="0.85"
      />
    </g>
  );
}

function VaciadoManual({ x, y }: { x: number; y: number }) {
  return (
    <g opacity="0.7">
      <line x1={x} y1={y} x2={x} y2={y + 22} stroke="#94a3b8" strokeWidth="2" strokeDasharray="3,2" />
      <polygon points={`${x},${y + 28} ${x - 5},${y + 20} ${x + 5},${y + 20}`} fill="#94a3b8" />
      <circle cx={x} cy={y - 8} r="7" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.5" />
      <text x={x} y={y - 5} fill="#64748b" fontSize="8" textAnchor="middle">🔧</text>
      <text x={x} y={y + 42} fill="#94a3b8" fontSize="7" textAnchor="middle" fontStyle="italic">manual</text>
    </g>
  );
}

/**
 * Dibuja 3 líneas de nivel dentro del depósito raceway:
 *   N0 = sensor_drain (vacío / fondo)
 *   N1 = sensor_min   (nivel mínimo)
 *   N2 = sensor_max   (nivel máximo)
 *
 * Depósito: x=640, y=240, w=100, h=130
 */
function NivelesBarra({
  x, y, w, h,
  sensorDrain,
  sensorMin,
  sensorMax,
}: {
  x: number; y: number; w: number; h: number;
  sensorDrain: boolean;
  sensorMin: boolean;
  sensorMax: boolean;
}) {
  const yMax   = y + h * 0.20;
  const yMin   = y + h * 0.57;
  const yDrain = y + h * 0.90;

  const lineProps = (active: boolean) => ({
    stroke: active ? '#4ade80' : '#94a3b8',
    strokeWidth: active ? 1.5 : 1,
    strokeDasharray: '4,3' as string,
    opacity: active ? 1 : 0.45,
  });

  return (
    <g>
      <line x1={x} y1={yMax}   x2={x + w} y2={yMax}   {...lineProps(sensorMax)}   />
      <line x1={x} y1={yMin}   x2={x + w} y2={yMin}   {...lineProps(sensorMin)}   />
      <line x1={x} y1={yDrain} x2={x + w} y2={yDrain} {...lineProps(sensorDrain)} />
    </g>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function RacewaySVG({ estado, onValvula }: RacewaySVGProps) {
  const sinDatos = !estado;

  // Tanques — solo usamos propiedades que existen en TankSnapshot
  const r = estado?.tanks?.dep_raceway;
  const d = estado?.tanks?.dep_cultivo;
  const s = estado?.tanks?.dep_cosecha;

  // ── Estados de válvulas desde el array estado.valves (VALVE_ADDR) ──────────
  // NUNCA leer fill_valve/gas_valve/drain_valve desde TankSnapshot: no existen.
  const valves = estado?.valves;
  const v1Open = getValveOpen(valves, VALVE_ADDR.V1_FILL_RACEWAY);     // addr 16
  const v2Open = getValveOpen(valves, VALVE_ADDR.V2_FILL_CULTIVO);     // addr 17
  const v3Open = getValveOpen(valves, VALVE_ADDR.V3_GAS_CULTIVO);      // addr 18
  const v4Open = getValveOpen(valves, VALVE_ADDR.V4_TRANSFER_RACEWAY); // addr 19
  const v5Open = getValveOpen(valves, VALVE_ADDR.V5_DRAIN_CULTIVO);    // addr 20
  const v6Open = getValveOpen(valves, VALVE_ADDR.V6_FILL_COSECHA);     // addr 21

  // ── Sensores dep_raceway (3 niveles) ────────────────────────────────────────
  // sensor_drain → N0 vacío/fondo  (null si el tank no lo tiene)
  // sensor_min   → N1 mínimo       (null si el tank no lo tiene)
  // sensor_max   → N2 máximo
  const rDrain = r?.sensor_drain ?? false;
  const rMin   = r?.sensor_min   ?? false;
  const rMax   = r?.sensor_max   ?? false;

  // ── Colores ─────────────────────────────────────────────────────────────────
  const C = {
    fondo:         '#f0f9ff',
    tuberia:       '#0369a1',
    borde:         '#075985',
    bordeGrosor:   2.5,
    label:         '#0c4a6e',
    canalAgua:     '#bae6fd',
    canalBorde:    '#075985',
    canalInterior: '#f0f9ff',
  };

  const track = { x: 200, y: 30, w: 440, h: 120, r: 20 };
  const inner = { x: 245, y: 55, w: 350, h: 70 };
  const flowColor = sinDatos ? '#94a3b8' : C.tuberia;

  const arrows = [
    { x: 340, y: 41,  angle: 0   },
    { x: 460, y: 41,  angle: 0   },
    { x: 460, y: 139, angle: 180 },
    { x: 340, y: 139, angle: 180 },
    { x: 212, y: 90,  angle: 90  },
    { x: 628, y: 90,  angle: 270 },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        bgcolor: C.fondo,
        p: { xs: 1, md: 2 },
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 860 610"
        preserveAspectRatio="xMidYMid meet"
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: 'calc(100vh - 200px)',
          display: 'block',
          fontFamily: 'monospace',
          shapeRendering: 'geometricPrecision',
        }}
      >
        <rect width="860" height="610" fill={C.fondo} rx="12" />

        {/* ══ CANAL RACEWAY ══ */}
        <rect
          x={track.x} y={track.y} width={track.w} height={track.h} rx={track.r}
          fill={C.canalAgua} stroke={C.canalBorde} strokeWidth="3"
        />
        <rect
          x={inner.x} y={inner.y} width={inner.w} height={inner.h} rx="8"
          fill={C.canalInterior} stroke={C.canalBorde} strokeWidth="1.5"
        />
        <text x="420" y="95" fill={C.label} fontSize="11" textAnchor="middle" fontWeight="700">
          RACEWAY
        </text>

        {arrows.map((a, i) => <FlowArrow key={i} {...a} color={flowColor} />)}

        {/* Animación de flujo activo */}
        {!sinDatos && (
          <g opacity="0.6">
            <line
              x1={track.x + track.r} y1={track.y + 11}
              x2={track.x + track.w - track.r} y2={track.y + 11}
              stroke="#7dd3fc" strokeWidth="2" strokeDasharray="12,8"
            >
              <animate attributeName="stroke-dashoffset" values="0;-80" dur="1.5s" repeatCount="indefinite" />
            </line>
            <line
              x1={track.x + track.r} y1={track.y + track.h - 11}
              x2={track.x + track.w - track.r} y2={track.y + track.h - 11}
              stroke="#7dd3fc" strokeWidth="2" strokeDasharray="12,8"
            >
              <animate attributeName="stroke-dashoffset" values="0;80" dur="1.5s" repeatCount="indefinite" />
            </line>
          </g>
        )}

        {/* ══ DEP. RACEWAY — 3 niveles ══ */}
        <rect x="640" y="240" width="100" height="130"
          fill="white" stroke={C.borde} strokeWidth={C.bordeGrosor} rx="4"
        />
        <AguaSVG x={641} y={241} width={98} height={128}
          nivel={r?.level ?? 0} encendido={false}
        />

        {/* Líneas de nivel N0/N1/N2 dentro del depósito */}
        <NivelesBarra
          x={641} y={241} w={98} h={128}
          sensorDrain={rDrain}
          sensorMin={rMin}
          sensorMax={rMax}
        />

        <text x="690" y="232" fill={C.label} fontSize="9" textAnchor="middle" fontWeight="700">
          DEP. RACEWAY
        </text>

        {/* Indicadores laterales: N2 (max), N1 (min), N0 (drain) */}
        <SensorSVG x={618} y={268} activo={rMax}   label="N2" />
        <SensorSVG x={618} y={313} activo={rMin}   label="N1" />
        <SensorSVG x={618} y={358} activo={rDrain} label="N0" />

        {/* Etiquetas de nivel a la derecha del depósito */}
        <text x="750" y="270" fill={rMax   ? '#16a34a' : '#94a3b8'} fontSize="7" fontFamily="monospace">MAX</text>
        <text x="750" y="315" fill={rMin   ? '#16a34a' : '#94a3b8'} fontSize="7" fontFamily="monospace">MIN</text>
        <text x="750" y="360" fill={rDrain ? '#16a34a' : '#94a3b8'} fontSize="7" fontFamily="monospace">DRN</text>

        {/* Tuberías Dep. Raceway */}
        <path d="M 690,240 L 690,178 L 600,178 L 600,150" fill="none" stroke={C.tuberia} strokeWidth="5" />
        <line x1="690" y1="370" x2="690" y2="430" stroke={C.tuberia} strokeWidth="5" />

        {/* V1 — llenado raceway | bloqueada si sensor_max activo */}
        <ValvulaSVG
          x={690} y={400}
          abierta={v1Open}
          bloqueada={rMax || sinDatos}
          label="V1"
          onClick={() => !sinDatos && onValvula('v1', v1Open)}
        />
        <VaciadoManual x={755} y={290} />

        {/* ══ DEP. CULTIVO ══ */}
        <polygon points="60,220 160,220 135,370 85,370"
          fill="white" stroke={C.borde} strokeWidth={C.bordeGrosor}
        />
        {(d?.level ?? 0) > 0 && (
          <g clipPath="url(#clip-cultivo)">
            <defs>
              <clipPath id="clip-cultivo">
                <polygon points="60,220 160,220 135,370 85,370" />
              </clipPath>
            </defs>
            <rect
              x="60"
              y={370 - (150 * (d?.level ?? 0)) / 100}
              width="100"
              height={(150 * (d?.level ?? 0)) / 100}
              fill="#34d399"
              opacity="0.35"
            />
          </g>
        )}
        <text x="110" y="212" fill={C.label} fontSize="9" textAnchor="middle" fontWeight="700">
          DEP. CULTIVO
        </text>

        {/* Sensores cultivo: sensor_min=N3, sensor_max=N4 */}
        <SensorSVG x={24} y={320} activo={d?.sensor_min ?? false} label="N3" />
        <SensorSVG x={24} y={272} activo={d?.sensor_max ?? false} label="N4" />

        {/* V2 — llenado cultivo */}
        <path d="M 640,305 L 420,305 L 420,275 L 160,275" fill="none" stroke={C.tuberia} strokeWidth="4" />
        <ValvulaSVG
          x={510} y={305}
          abierta={v2Open}
          bloqueada={sinDatos}
          label="V2"
          labelPos="below"
          onClick={() => !sinDatos && onValvula('v2', v2Open)}
        />

        {/* V3 — gases O₂+CO₂ */}
        <path d="M 60,248 L 20,248 L 20,195" fill="none" stroke={C.tuberia} strokeWidth="4" />
        <ValvulaSVG
          x={20} y={220}
          abierta={v3Open}
          bloqueada={sinDatos}
          label="V3"
          labelPos="above"
          onClick={() => !sinDatos && onValvula('v3', v3Open)}
        />

        {/* V4 — traspaso → raceway */}
        <path d="M 110,220 L 110,168 L 200,168 L 200,150" fill="none" stroke={C.tuberia} strokeWidth="4" />
        <ValvulaSVG
          x={140} y={168}
          abierta={v4Open}
          bloqueada={sinDatos}
          label="V4"
          labelPos="above"
          onClick={() => !sinDatos && onValvula('v4', v4Open)}
        />

        {/* V5 — vaciado exterior cultivo */}
        <line x1="97" y1="370" x2="80" y2="418" stroke={C.tuberia} strokeWidth="4" />
        <ValvulaSVG
          x={80} y={392}
          abierta={v5Open}
          bloqueada={sinDatos}
          label="V5"
          onClick={() => !sinDatos && onValvula('v5', v5Open)}
        />

        {/* ══ DEP. COSECHA ══ */}
        <rect x="300" y="420" width="130" height="115"
          fill="white" stroke={C.borde} strokeWidth={C.bordeGrosor} rx="4"
        />
        {/* level (no nivel) y sensor_max (no sensor_maximo) */}
        <AguaSVG x={301} y={421} width={128} height={113}
          nivel={s?.level ?? 0} encendido={false}
        />
        <text x="365" y="412" fill={C.label} fontSize="9" textAnchor="middle" fontWeight="700">
          DEP. COSECHA
        </text>

        {/* sensor_max (no sensor_maximo) */}
        <SensorSVG x={442} y={460} activo={s?.sensor_max ?? false} label="N5" />

        {/* V6 — llenado cosecha | bloqueada si sensor_max activo */}
        <path d="M 128,370 L 128,462 L 300,462" fill="none" stroke={C.tuberia} strokeWidth="4" />
        <ValvulaSVG
          x={205} y={462}
          abierta={v6Open}
          bloqueada={(s?.sensor_max ?? false) || sinDatos}
          label="V6"
          labelPos="below"
          onClick={() => !sinDatos && onValvula('v6', v6Open)}
        />
        <VaciadoManual x={462} y={468} />

        {/* ── Overlay sin conexión ── */}
        {sinDatos && (
          <Fade in={sinDatos}>
            <g>
              <rect x="0" y="0" width="860" height="610" fill="rgba(224, 242, 254, 0.45)" rx="12" />
              <rect x="280" y="270" width="300" height="60" rx="8" fill="white" stroke="#bfdbfe" />
              <text x="430" y="295" fill="#0c4a6e" fontSize="14" textAnchor="middle" fontWeight="600">
                Sin conexión con la API
              </text>
              <text x="430" y="315" fill="#64748b" fontSize="11" textAnchor="middle">
                Datos no disponibles
              </text>
            </g>
          </Fade>
        )}
      </Box>
    </Paper>
  );
}